import {
  CONSENT_COOKIE_MAX_AGE_SECONDS,
  CONSENT_COOKIE_NAME,
  ConsentRegime,
  ConsentSource,
  ConsentState,
  StoredConsent,
  decodeConsentCookie,
  encodeConsentCookie,
  storedToConsentState,
} from "@shared/consent";

/**
 * Reading and writing the consent cookie.
 *
 * A cookie rather than localStorage for two reasons: the inline snippet in
 * index.html has to read the choice synchronously before GTM loads, and the
 * choice has to be shared between outclose.com and app.outclose.com, which
 * localStorage cannot do.
 */

const ROOT_DOMAIN = "outclose.com";

/**
 * `.outclose.com` in production so a choice made on a landing page survives the
 * hop to the app subdomain, exactly like the PostHog `cross_subdomain_cookie`.
 * Host-only anywhere else: browsers reject a Domain attribute that isn't a
 * suffix of the current host, which would drop the cookie entirely on
 * localhost and on *.pages.dev preview builds.
 */
function cookieDomain(): string | null {
  const host = window.location.hostname;
  if (host === ROOT_DOMAIN || host.endsWith(`.${ROOT_DOMAIN}`)) {
    return `.${ROOT_DOMAIN}`;
  }
  return null;
}

function readRawCookie(name: string): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${name}=([^;]*)`));
  return match ? match[1] : null;
}

export function readStoredConsent(): StoredConsent | null {
  return decodeConsentCookie(readRawCookie(CONSENT_COOKIE_NAME));
}

export function readStoredConsentState(): ConsentState | null {
  const stored = readStoredConsent();
  return stored ? storedToConsentState(stored) : null;
}

export function writeConsentCookie(
  state: ConsentState,
  regime: ConsentRegime,
  source: ConsentSource
): void {
  const domain = cookieDomain();
  const parts = [
    `${CONSENT_COOKIE_NAME}=${encodeConsentCookie(state, regime, source)}`,
    "path=/",
    `max-age=${CONSENT_COOKIE_MAX_AGE_SECONDS}`,
    // Lax rather than Strict: the cookie has to be present on the first request
    // after an ad click lands the user here from an external site, otherwise
    // the banner reappears for someone who already answered it.
    "SameSite=Lax",
  ];
  if (domain) parts.push(`domain=${domain}`);
  if (window.location.protocol === "https:") parts.push("Secure");
  document.cookie = parts.join("; ");
}

/**
 * Deletes a cookie by name across the variants a third-party script may have
 * written it under. A cookie is only removed if the domain and path of the
 * expiring write match the original exactly, and we don't know which the vendor
 * used, so all plausible combinations are attempted.
 */
export function deleteCookie(name: string): void {
  const host = window.location.hostname;
  const domains: (string | null)[] = [null, host, `.${host}`];
  const root = cookieDomain();
  if (root) domains.push(root, root.slice(1));

  for (const domain of domains) {
    const parts = [`${name}=`, "path=/", "expires=Thu, 01 Jan 1970 00:00:01 GMT"];
    if (domain) parts.push(`domain=${domain}`);
    document.cookie = parts.join("; ");
  }
}

/** Every cookie currently set whose name starts with one of `prefixes`. */
export function deleteCookiesWithPrefixes(prefixes: string[]): void {
  const names = document.cookie
    .split(";")
    .map((chunk) => chunk.split("=")[0]?.trim())
    .filter((name): name is string => !!name);

  for (const name of names) {
    if (prefixes.some((prefix) => name.startsWith(prefix))) {
      deleteCookie(name);
    }
  }
}
